import React, { useState, useEffect } from 'react';
import { usePWA } from '../../hooks/usePWA';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  Slide,
  SlideProps
} from '@mui/material';
import { 
  SystemUpdate as UpdateIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';

interface PWAUpdateNotificationProps {
  onUpdate?: () => void;
  onDismiss?: () => void;
  autoHideDuration?: number;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

const PWAUpdateNotification: React.FC<PWAUpdateNotificationProps> = ({
  onUpdate,
  onDismiss,
  autoHideDuration = null
}) => {
  const { isUpdateAvailable, reloadApp } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when update becomes available
  useEffect(() => {
    if (isUpdateAvailable) {
      setDismissed(false);
    }
  }, [isUpdateAvailable]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      // Send message to service worker to skip waiting
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
      
      // Wait a moment for the service worker to take control
      setTimeout(() => {
        onUpdate?.();
        reloadApp();
      }, 100);
    } catch (error) {
      console.error('Failed to update app:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  if (!isUpdateAvailable || dismissed) {
    return null;
  }

  return (
    <Snackbar
      open={true}
      autoHideDuration={autoHideDuration}
      onClose={handleDismiss}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        '& .MuiAlert-root': {
          minWidth: 350,
          borderRadius: 2
        }
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<UpdateIcon />}
        onClose={handleDismiss}
        action={
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleUpdate}
              disabled={isUpdating}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        }
        sx={{
          backgroundColor: 'primary.main',
          '& .MuiAlert-message': {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          New Version Available
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          A new version of StageLink is ready to install
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default PWAUpdateNotification;
